import React from 'react'
import { Paper, Stack, Tab, Tabs, Typography } from '@mui/material'
import { Outlet, useLocation, Link as RouterLink, useOutletContext } from 'react-router-dom'
import { usePageTitle } from '../components/common/PageTitleContext'
import type { AppOutletContext } from '../app/AppShell'

type SuperadminTab = {
  value: string
  label: string
  description?: string
}

const SUPERADMIN_TABS: SuperadminTab[] = [
  {
    value: '/superadmin/general/weather',
    label: 'Global weather settings',
    description: 'Configure OpenWeather credentials, scheduling, and rate limit monitoring.',
  },
  {
    value: '/superadmin/general/ytmp3',
    label: 'YT-MP3 Downloader',
    description: 'Manage RapidAPI credentials, credit windows, playlists, and cleanup retention.',
  },
]

const Superadmin: React.FC = () => {
  const appContext = useOutletContext<AppOutletContext>()
  const { includeSuperadmin } = appContext
  const location = useLocation()

  usePageTitle('Superadmin - General')

  const activeTab = React.useMemo(() => {
    return SUPERADMIN_TABS.find((tab) => location.pathname.startsWith(tab.value)) ?? SUPERADMIN_TABS[0]
  }, [location.pathname])

  if (!includeSuperadmin) {
    return (
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Stack spacing={1}>
          <Typography variant="h5" component="h1">
            Access denied
          </Typography>
          <Typography color="text.secondary">
            The superadmin console is limited to users with the superadmin or WordPress administrator capability.
          </Typography>
        </Stack>
      </Paper>
    )
  }

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography variant="h4" component="h1">
          Superadmin - General
        </Typography>
        <Typography color="text.secondary">
          Manage global configuration and advanced controls across all stations.
        </Typography>
      </Stack>
      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        <Stack spacing={0}>
          <Tabs
            value={activeTab.value}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="Superadmin general subtabs"
            sx={{ px: 1 }}
          >
            {SUPERADMIN_TABS.map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                label={tab.label}
                component={RouterLink}
                to={tab.value}
              />
            ))}
          </Tabs>
          {activeTab.description ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: 3, py: 2 }}>
              {activeTab.description}
            </Typography>
          ) : null}
        </Stack>
      </Paper>
      <Outlet context={appContext} />
    </Stack>
  )
}

export default Superadmin
